/**
 * Local Experience Item Component
 * Displays individual local experience with image and description
 * Follows Single Responsibility Principle - only responsible for rendering a single experience item
 */

import React from 'react';
import {
  spacing,
  borders,
  typography,
  typographyColors,
  strokeColors,
  backgroundColors,
} from '@/design-system/utils';
import { ImageThumbnail } from '@/components/ui/image-thumbnail';
import { ExpandableText } from '@/components/ui/expandable-text';
import { Heading } from '@/components/ui/heading';
import { LocalExperience } from './types';

export interface LocalExperienceItemProps {
  /**
   * Local experience data
   */
  experience: LocalExperience;
  /**
   * Place name for image fetching context
   */
  placeName?: string;
  /**
   * Whether to use mobile layout
   */
  isMobile?: boolean;
}

/**
 * Local Experience Item Component
 * Renders a single local experience with image, title, description, and price
 * Matches Figma design: horizontal layout with thumbnail on left, text on right
 */
export const LocalExperienceItem: React.FC<LocalExperienceItemProps> = ({
  experience,
  placeName,
}) => {
  const thumbnailWidth = 82;
  const thumbnailHeight = 74;

  return (
    <div
      style={{
        display: 'flex',
        padding: spacing[3],
        alignItems: 'flex-start',
        gap: spacing[3],
        alignSelf: 'stretch',
        borderRadius: borders.radius.xl,
        border: `1px solid ${strokeColors.lightest}`,
        backgroundColor: backgroundColors.primary,
      }}
    >
      {/* Thumbnail Image - 84px x 72px */}
      <div
        style={{
          position: 'relative',
          width: thumbnailWidth,
          height: thumbnailHeight,
          borderRadius: borders.radius.md,
          overflow: 'hidden',
          flexShrink: 0,
        }}
      >
        <ImageThumbnail
          image={experience.image}
          alt={experience.title}
          title={experience.title}
          description={experience.description}
          placeName={placeName}
          category='Local Experiences'
          width={`${thumbnailWidth}px`}
          height={`${thumbnailHeight}px`}
          onImageLoad={() => {
            console.log('✅ [LocalExperienceItem] Image loaded in DOM:', {
              experienceTitle: experience.title,
              experienceId: experience.id,
            });
          }}
        />
      </div>

      {/* Content - Title, Description, Price */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: spacing[1],
          flex: 1,
          minWidth: 0, // Allow text to wrap
        }}
      >
        {/* Title */}
        <Heading
          as='h4'
          fontSize={typography.size.base}
          fontWeight={typography.weight.semibold}
          color={typographyColors.text}
          lineHeight={typography.lineHeight.normal}
        >
          {experience.title}
        </Heading>

        {/* Description */}
        {experience.description && (
          <ExpandableText
            text={experience.description}
            maxLines={2}
            fontSize={typography.size.sm}
            fontWeight={typography.weight.regular}
            color={typographyColors.text}
            lineHeight={typography.lineHeight.relaxed}
          />
        )}
      </div>
    </div>
  );
};
